/**
 * Shared bulk-upload modal — downloads an Excel template, parses uploaded
 * .xlsx/.csv, and POSTs `${basePath}/bulk-import` with { rows }.
 * Props:
 *   basePath        — e.g. "/subcontractors" or "/ein"
 *   entityLabel     — display label, e.g. "vendors" / "entities"
 *   templateCols    — [{ key, label, sample?, numeric?, date? }, …]
 *   templateFilename — string, e.g. "STRATA SUBCONTRACTOR TEMPLATE.xlsx"
 *   testIdPrefix
 */
import { useState } from "react";
import * as XLSX from "xlsx";
import api, { apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileArrowDown, FileArrowUp, CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function BulkUploadModal({ open, onClose, onImported, basePath, entityLabel = "rows", templateCols = [], templateFilename = "STRATA TEMPLATE.xlsx", testIdPrefix = "bulk-upload" }) {
  const [rows, setRows] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const header = templateCols.map((c) => c.label);
    const example = templateCols.map((c) => c.sample ?? "");
    const ws = XLSX.utils.aoa_to_sheet([header, example]);
    ws["!cols"] = templateCols.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, templateFilename);
  };

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array", cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const arr = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const labelToKey = new Map(templateCols.map((c) => [c.label.toLowerCase(), c]));
        const parsed = arr.map((r) => {
          const out = {};
          for (const [k, v] of Object.entries(r)) {
            const norm = String(k).trim().toLowerCase();
            const col = labelToKey.get(norm);
            if (!col) continue;
            if (v === "" || v == null) continue;
            if (col.numeric) {
              const n = Number(v);
              if (!isNaN(n)) out[col.key] = n;
            } else if (col.date) {
              if (v instanceof Date) out[col.key] = v.toISOString().slice(0, 10);
              else {
                const d = new Date(v);
                if (!isNaN(d)) out[col.key] = d.toISOString().slice(0, 10);
                else out[col.key] = String(v).slice(0, 10);
              }
            } else {
              out[col.key] = String(v).trim();
            }
          }
          return out;
        }).filter((r) => Object.keys(r).length > 0);
        setRows(parsed);
        setResult(null);
      } catch (err) {
        toast.error(`Could not read that file: ${err.message}`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const submit = async () => {
    if (!rows?.length) return;
    setUploading(true);
    try {
      const { data } = await api.post(`${basePath}/bulk-import`, { rows });
      setResult(data);
      toast.success(`Imported ${data.created} new ${entityLabel}; skipped ${data.skipped}.`);
      onImported && onImported();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid={`${testIdPrefix}-modal`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileArrowUp size={18} className="text-[#327de3]" /> Bulk upload {entityLabel}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
            <strong>How to use:</strong>
            <ol className="list-decimal ml-4 mt-1 space-y-0.5">
              <li>Download the template.</li>
              <li>Fill in one row per {entityLabel.replace(/s$/, "")} — the column headers must not be changed.</li>
              <li>Upload the completed file. Duplicate rows are skipped automatically.</li>
            </ol>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadTemplate} data-testid={`${testIdPrefix}-download-template`}>
              <FileArrowDown size={14} className="mr-1.5" /> Download template
            </Button>
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
              <FileArrowUp size={14} /> Choose file
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) parseFile(f); e.target.value = ""; }} data-testid={`${testIdPrefix}-file-input`} />
            </label>
            {rows && <span className="text-xs text-slate-500">Parsed {rows.length} row{rows.length === 1 ? "" : "s"} — ready to import.</span>}
          </div>
          {rows && rows.length > 0 && (
            <div className="rounded-md border border-slate-200 overflow-hidden">
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      {templateCols.slice(0, 8).map((c) => <th key={c.key} className="text-left px-2 py-1 border-b border-slate-200 font-semibold text-slate-600">{c.label}</th>)}
                      <th className="text-left px-2 py-1 border-b border-slate-200 font-semibold text-slate-600">…</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 25).map((r, i) => (
                      <tr key={i} className="odd:bg-white even:bg-slate-50/40">
                        {templateCols.slice(0, 8).map((c) => <td key={c.key} className="px-2 py-0.5 border-b border-slate-100 whitespace-nowrap">{r[c.key] ?? ""}</td>)}
                        <td className="px-2 py-0.5 border-b border-slate-100 text-slate-400">…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 25 && <div className="text-[10px] text-slate-500 italic px-2 py-1">+ {rows.length - 25} more row(s) — will be included.</div>}
              </div>
            </div>
          )}
          {result && (
            <div className="rounded-md border border-slate-200 p-3 text-xs" data-testid={`${testIdPrefix}-result`}>
              <div className="flex items-center gap-4 mb-2">
                <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle size={14} /> Created: {result.created}</span>
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><WarningCircle size={14} /> Skipped: {result.skipped}</span>
              </div>
              {result.skipped_items?.length > 0 && (
                <details>
                  <summary className="text-slate-500 cursor-pointer text-[11px]">Show skipped rows and reasons</summary>
                  <ul className="mt-1 space-y-0.5">
                    {result.skipped_items.slice(0, 20).map((s, i) => (
                      <li key={i} className="text-[10px] text-slate-600">
                        <strong>{s.reason}</strong> — {JSON.stringify(s.row).slice(0, 80)}…
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} disabled={!rows?.length || uploading} className="bg-[#327de3] hover:bg-[#1f64c2]" data-testid={`${testIdPrefix}-submit`}>
            {uploading ? "Importing…" : `Import ${rows?.length || 0} row(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
